import gfcfThemeConfig from 'gfcf-theme-config';
import { getNode, getNodes, saferHtml } from '@gravityforms/utils';
import elements from './elements';
import state from './state';

/**
 * @function showValidationError
 * @description Shows a field validation error message.
 *
 * @since 1.0.0
 *
 * @param {number} formId       The form ID.
 * @param {number} fieldId      The field ID that failed validation.
 * @param {string} errorMessage The error message to be displayed.
 */
export const showValidationError = ( formId, fieldId, errorMessage ) => {
	elements.fields[ state.activeFieldIndex ].classList.add( 'gfield_error' );
	elements.fields[ state.activeFieldIndex ].insertAdjacentHTML(
		'beforeend',
		saferHtml`<div id="validation_message_${ formId }_${ fieldId }" class="gfield_description gfield_validation_message">${ errorMessage }</div>`
	);
};

/**
 * @function hideValidationError
 * @description Hides any validation error that is currently being displayed.
 *
 * @since 1.0.0
 *
 */
export const hideValidationError = () => {
	// Hiding global error message.
	getNodes( '.gform_validation_errors', true, document, true )
		.forEach( ( item ) => item.style.display = 'none' );

	// Removing field error message
	elements.fields[ state.activeFieldIndex ].classList.remove( 'gfield_error' );
	getNodes( '.gfield_validation_message', true, elements.fields[ state.activeFieldIndex ], true )
		.forEach( ( item ) => item.remove() );
};

/**
 * @function isElementInCurrentField
 * @description Determines if the specified form element with name `elementName` is in the current field.
 *
 * @since 1.0.0
 *
 * @param {string} elementName The element name to be tested for the current field.
 *
 * @return {boolean} Returns true if `elementName` is in the current field. Otherwise, returns false.
 */
export const isElementInCurrentField = ( elementName ) => {
	if ( ! elements.fieldInputs[ state.activeFieldIndex ] ) {
		elements.fieldInputs[ state.activeFieldIndex ] = getNodes(
			'input, select, textarea',
			true,
			elements.fields[ state.activeFieldIndex ],
			true,
		);
	}

	const exists = elements.fieldInputs[ state.activeFieldIndex ].reduce( ( carry, input ) => {
		return carry || input.name === elementName;
	}, false );

	return exists;
};

/**
 * @function getFormData
 * @description Creates a FormData object comprised of just fields needed to validate the specified fieldId
 *
 * @since 1.0.0
 *
 * @param {number} fieldId The field ID to be included in the FormData object.
 *
 * @return {FormData} Returns a trimmed FormData object with data just for the specified fieldId.
 */
export const getFormData = ( fieldId ) => {
	const formData = new FormData( elements.form );

	for ( const key of formData.keys() ) {
		if ( isElementInCurrentField( key ) ) {
			continue;
		}

		formData.delete( key );
	}

	const formId = getNode( 'input[name="gform_submit"]', elements.form, true ).value;
	formData.append( 'form_id', formId );
	formData.append( 'action', 'gfcf_validate_field' );
	formData.append( 'field_id', fieldId );

	return formData;
};

/**
 * @function validate
 * @description Performs an AJAX request to validate the specified field.
 *
 * @since 1.0.0
 *
 * @param {number} fieldId The field ID to be validated.
 *
 * @return {string} Error message
 */
export const validate = async ( fieldId ) => {
	const formData = getFormData( fieldId );

	try {
		const result = await fetch(
			gfcfThemeConfig?.endpoints?.ajaxurl || '',
			{
				method: 'POST',
				credentials: 'same-origin',
				body: formData,
			}
		);
		const json = await result.json();
		return json.data;
	} catch ( e ) {
		return gfcfThemeConfig?.i18n?.unknown_error || '';
	}
};
